import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiClose, mdiPackageVariantClosed } from '@mdi/js'
import { $api } from '../../api/queryClient'
import type { ImportRow } from './importTypes'

interface ImportFilterBadgeProps {
    org: string
    importUid: string
    onClear: () => void
}

/**
 * Visible, clearable chip for the documents list' `?import_uid=` filter
 * (spec 2026-08-16-05 §4).
 *
 * Without it the drill-down from an import dashboard lands on a list that
 * *looks* unfiltered — the funnel numbers would then read as a bug rather than
 * as a filter.
 */
export function ImportFilterBadge({ org, importUid, onClear }: ImportFilterBadgeProps) {
    const { t } = useTranslation('imports')

    const { data } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/imports',
        { params: { path: { orgCode: org }, query: { limit: 200 } } },
    )

    const rows = ((data as any)?.data ?? []) as ImportRow[]
    // Falls back to the uid while the list loads (or if the import was
    // deleted): the chip must appear as soon as the filter is on.
    const label = rows.find(row => row.uid === importUid)?.label ?? importUid

    return (
        <span
            className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-3 py-1 text-sm text-foreground"
            data-testid="documents-import-filter"
        >
            <AppIcon tone="muted" path={mdiPackageVariantClosed} size="xs" />
            {t('filter.label', { label })}
            <button
                type="button"
                onClick={onClear}
                aria-label={t('filter.clear')}
                title={t('filter.clear')}
                className="ml-1 rounded-full text-muted-foreground hover:text-foreground"
                data-testid="documents-import-filter-clear"
            >
                <AppIcon path={mdiClose} size="xs" />
            </button>
        </span>
    )
}
